import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ClientImplementation, KanbanColumn } from '@/types/customerSupport';
import ImplementationCard from './ImplementationCard';

interface ImplementationKanbanColumnProps {
  column: KanbanColumn;
  implementations: ClientImplementation[];
  columnsByKey: Record<string, KanbanColumn>;
  onImplementationClick: (implementation: ClientImplementation) => void;
  onViewObservations?: (implementation: ClientImplementation) => void;
  searchTerm?: string;
}

const ImplementationKanbanColumn: React.FC<ImplementationKanbanColumnProps> = ({
  column,
  implementations,
  columnsByKey,
  onImplementationClick,
  onViewObservations,
  searchTerm = '',
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: column.key,
  });

  const filteredImplementations = implementations.filter(impl => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const client = impl.client;
    return (
      client?.legal_name?.toLowerCase().includes(term) ||
      client?.trade_name?.toLowerCase().includes(term) ||
      client?.company_tax_id?.includes(term)
    );
  });

  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-80 transition-all ${isOver ? 'opacity-75' : ''}`}
    >
      <Card className="h-full border-t-4" style={{ borderTopColor: column.color }}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold">{column.label}</CardTitle>
            <Badge
              variant="secondary"
              className="rounded-full"
              style={{
                backgroundColor: `${column.color}20`,
                color: column.color,
              }}
            >
              {filteredImplementations.length}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="pt-0">
          <SortableContext items={filteredImplementations.map(i => i.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3 max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
              {filteredImplementations.map(implementation => (
                <ImplementationCard
                  key={implementation.id}
                  implementation={implementation}
                  columnsByKey={columnsByKey}
                  onClick={() => onImplementationClick(implementation)}
                  onViewObservations={() => onViewObservations?.(implementation)}
                />
              ))}

              {filteredImplementations.length === 0 && (
                <div className="text-center text-sm text-muted-foreground py-8">
                  {searchTerm ? 'Nenhuma implantação encontrada' : 'Nenhuma implantação nesta fase'}
                </div>
              )}
            </div>
          </SortableContext>
        </CardContent>
      </Card>
    </div>
  );
};

export default ImplementationKanbanColumn;
